import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './AddListingPage.module.css';

const FEATURES_LIST = [
  { key: 'balkon', label: 'Balkon' }, { key: 'taras', label: 'Taras' },
  { key: 'garaz', label: 'Garaż/Miejsce' }, { key: 'winda', label: 'Winda' },
  { key: 'ogrod', label: 'Ogród' }, { key: 'klimatyzacja', label: 'Klimatyzacja' },
  { key: 'umeblowane', label: 'Umeblowane' }, { key: 'piwnica', label: 'Piwnica' },
];

export default function AddListingPage() {
  const navigate = useNavigate();
  const { isLoggedIn, token, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    title: '', type: 'sprzedaz', price: '', area: '', rooms: '2',
    floor: '1 piętro', year: new Date().getFullYear(),
    location: '', address: '', description: '', features: [],
  });
  const [photos, setPhotos] = useState([]); // { file, preview }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!authLoading && !isLoggedIn) navigate('/logowanie');
  }, [isLoggedIn, authLoading, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleFeature = (key) => setForm(f => ({
    ...f,
    features: f.features.includes(key) ? f.features.filter(x => x !== key) : [...f.features, key],
  }));

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 3 - photos.length;
    const toAdd = files.slice(0, remaining).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(p => [...p, ...toAdd]);
    e.target.value = '';
  };

  const removePhoto = (i) => {
    URL.revokeObjectURL(photos[i].preview);
    setPhotos(p => p.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // 1. Utwórz ogłoszenie
      const res = await fetch('/api/listings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Błąd serwera');

      // 2. Wgraj zdjęcia jeśli są
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach(p => fd.append('images', p.file));
        await fetch(`/api/images/${data.id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      }

      setDone(true);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (authLoading) return null;

  if (done) return (
    <div className={styles.page}>
      <div className={styles.successBox}>
        <div className={styles.successIcon}>✓</div>
        <h2>Ogłoszenie zostało dodane!</h2>
        <p>Ogłoszenie oczekuje na akceptację administratora. Pojawi się na stronie po zatwierdzeniu.</p>
        <div className={styles.successActions}>
          <button className={styles.submitBtn} onClick={() => navigate('/moje-ogloszenia')}>
            Moje ogłoszenia
          </button>
          <button className={styles.cancelBtn} onClick={() => { setDone(false); setForm({ title:'', type:'sprzedaz', price:'', area:'', rooms:'2', floor:'1 piętro', year:new Date().getFullYear(), location:'', address:'', description:'', features:[] }); setPhotos([]); }}>
            Dodaj kolejne
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Dodaj ogłoszenie</h1>
        <p>Wypełnij formularz, aby opublikować swoją nieruchomość</p>
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>

        <div className={styles.section}>
          <h2>Typ ogłoszenia</h2>
          <div className={styles.typeGrid}>
            {[{val:'sprzedaz',label:'Sprzedaż',icon:'🏢'},{val:'wynajem',label:'Wynajem',icon:'🔑'},{val:'nowe',label:'Nowe budownictwo',icon:'🏗️'}].map(t => (
              <button type="button" key={t.val}
                className={`${styles.typeBtn} ${form.type === t.val ? styles.typeActive : ''}`}
                onClick={() => set('type', t.val)}>
                <span>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h2>Zdjęcia <span className={styles.optional}>opcjonalne · max 3</span></h2>
          <div className={styles.photoGrid}>
            {photos.map((p, i) => (
              <div key={i} className={styles.photoThumb}>
                <img src={p.preview} alt="" />
                <button type="button" className={styles.photoRemove} onClick={() => removePhoto(i)}>✕</button>
                {i === 0 && <span className={styles.photoMain}>Główne</span>}
              </div>
            ))}
            {photos.length < 3 && (
              <button type="button" className={styles.photoAdd} onClick={() => fileRef.current.click()}>
                <span>+</span>
                <span>Dodaj zdjęcie</span>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{display:'none'}} onChange={handlePhotos} />
        </div>

        <div className={styles.section}>
          <h2>Podstawowe informacje</h2>
          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <label>Tytuł ogłoszenia *</label>
              <input required type="text" placeholder="np. Przestronny apartament 3 pokoje | Mokotów" value={form.title} onChange={e => set('title', e.target.value)} />
            </div>
            <div className={styles.row2}>
              <div className={styles.field}>
                <label>Cena (zł) *</label>
                <input required type="number" min="1" placeholder="np. 650000" value={form.price} onChange={e => set('price', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Powierzchnia (m²) *</label>
                <input required type="number" min="1" placeholder="np. 75" value={form.area} onChange={e => set('area', e.target.value)} />
              </div>
            </div>
            <div className={styles.row3}>
              <div className={styles.field}>
                <label>Liczba pokoi</label>
                <select value={form.rooms} onChange={e => set('rooms', e.target.value)}>
                  {['1','2','3','4','5','6+'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Piętro</label>
                <select value={form.floor} onChange={e => set('floor', e.target.value)}>
                  {['Parter','1 piętro','2 piętro','3 piętro','4 piętro','5+ piętro','Ostatnie piętro','Dom'].map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Rok budowy</label>
                <input type="number" min="1900" max={new Date().getFullYear()} value={form.year} onChange={e => set('year', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Lokalizacja</h2>
          <div className={styles.row2}>
            <div className={styles.field}>
              <label>Miasto i dzielnica *</label>
              <input required type="text" placeholder="np. Warszawa, Mokotów" value={form.location} onChange={e => set('location', e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>Adres</label>
              <input type="text" placeholder="np. ul. Puławska 120" value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Opis</h2>
          <div className={styles.field}>
            <label>Opis nieruchomości</label>
            <textarea rows={5} placeholder="Opisz nieruchomość..." value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
        </div>

        <div className={styles.section}>
          <h2>Udogodnienia</h2>
          <div className={styles.featureGrid}>
            {FEATURES_LIST.map(f => (
              <label key={f.key} className={`${styles.featureLabel} ${form.features.includes(f.key) ? styles.featureActive : ''}`}>
                <input type="checkbox" checked={form.features.includes(f.key)} onChange={() => toggleFeature(f.key)} />
                {f.label}
              </label>
            ))}
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={() => navigate(-1)}>Anuluj</button>
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting ? 'Publikowanie...' : '✓ Opublikuj ogłoszenie'}
          </button>
        </div>
      </form>
    </div>
  );
}
